import Unit from "@r6digital/rapidstor-types/types/Unit";
import Location from "./Location";
import Rate from "@r6digital/rapidstor-types/types/Rate";
import { PostProcessedUnitTypeGroup } from "@r6digital/rapidstor-types/types/UnitType";
import DiscountPlan from "@r6digital/rapidstor-types/types/DiscountPlan";

export type UnitData = {
  unitType: UnitType;
};
export default interface UnitType {
  image: string;
  corpCode: string;
  locationCode: string;
  unitTypeId: number;
  typeName: string;
  deleted: boolean;
  stdRate: number;
  stdWeeklyRate: number;
  area: number;
  width: number;
  length: number;
  floor: number;
  inside: boolean;
  totalOccupied: number;
  totalReserved: number;
  totalUnits: number;
  totalVacant: number;
  totalAvailable: number;
  firstAvailableUnitName: string;
  representativeUnitName: string;
  reservationFee: number;
  units: Unit[];
  _id: string;
  deal?: Deal;
  rates: Rates;
}

export interface Deal {
  _id: string;
  title: string;
  descriptions: string[];
  discountPlan?: DiscountPlan;
}

export interface Rates {
  discounted: boolean;
  discountedRate: number;
  rate: number;
  taxInclusive: boolean;
}

export const ratesTransformer = (rates: Rate): Rates => {
  return <Rates>{
    discounted: rates.discounted,
    // Always set discountedRate, even if there's no discount.
    // It makes price comparison between units much simpler.
    discountedRate: rates.discounted ? rates.discountedRate : rates.rate,
    rate: rates.rate,
    taxInclusive: rates.taxInclusive,
  };
};

export const unitTypeTransformer = (
  unitType: PostProcessedUnitTypeGroup,
  location?: Location
): UnitType => {
  let deal: Deal | undefined = undefined;
  let rates: Rates;

  if (unitType.deals.length > 0) {
    const [firstDeal] = unitType.deals;
    deal = {
      _id: firstDeal._id,
      title: firstDeal.title,
      descriptions: firstDeal.descriptions,
    };

    if (firstDeal.discountPlan) {
      deal.discountPlan = firstDeal.discountPlan;
    }

    rates = ratesTransformer(unitType.deals[0].rates);
  } else {
    rates = ratesTransformer(unitType.rates);
  }

  let totalAvailable = unitType.iTotalVacant;
  if (location?.rapidstor.includeReservedUnitsInOccupied) {
    totalAvailable -= unitType.iTotalReserved;
  }

  return <UnitType>{
    image: unitType.descriptor?.picture ?? "",
    corpCode: unitType.sCorpCode,
    locationCode: unitType.sLocationCode,
    unitTypeId: unitType.UnitTypeID,
    typeName: unitType.sTypeName,
    deleted: unitType.deleted,
    stdRate: unitType.dcStdRate,
    stdWeeklyRate: unitType.dcStdWeeklyRate,
    area: unitType.dcArea,
    width: unitType.dcWidth,
    length: unitType.dcLength,
    floor: unitType.iFloor,
    inside: unitType.bInside,
    totalOccupied: unitType.iTotalOccupied,
    totalReserved: unitType.iTotalReserved,
    totalUnits: unitType.iTotalUnits,
    totalVacant: unitType.iTotalVacant,
    totalAvailable,
    firstAvailableUnitName: unitType.sUnitName_FirstAvailable,
    representativeUnitName: unitType.sUnitName_Representative,
    reservationFee: unitType.dcReservationFee,
    units: unitType.units,
    _id: unitType._id,
    deal,
    rates,
  };
};

export type UnitGroup = "ground" | "upper" | "driveup";
export type UnitGroups = Record<UnitGroup, UnitType | null>;

/**
 * Adapted from Rapidstor
 *
 * @link https://github.com/r6digital/rapidstor/blob/008a37c8f41fca2978f4f43792d6745f7cd2de61/public/application/scripts/publicPanel/features/unit-listing/common/services.js#L102
 */
export function determineFloor(unitType: UnitType, groundFloor = 0): UnitGroup {
  if (unitType.floor > groundFloor) {
    return "upper";
  } else if (unitType.inside) {
    return "ground";
  } else {
    return "driveup";
  }
}

/**
 * Adapted from Rapidstor
 *
 * @link https://github.com/r6digital/rapidstor/blob/008a37c8f41fca2978f4f43792d6745f7cd2de61/public/application/scripts/publicPanel/features/unit-listing/common/services.js#L115
 */
export function distributeUnitsToGroups(
  unitTypes: UnitType[],
  groundFloor = 0,
  preferFn?: (ut: UnitType) => boolean
): UnitGroups {
  const groups: UnitGroups = {
    ground: null,
    upper: null,
    driveup: null,
  };

  for (const unitType of unitTypes) {
    const group = determineFloor(unitType, groundFloor);
    if (groups[group] === null) {
      groups[group] = unitType;
      continue;
    }

    const existingUnitType = groups[group] as UnitType;

    // Availability is highest priority: bookable (totalAvailable) always beats non-bookable.
    // We intentionally use totalAvailable (not totalVacant) because a unit can be physically
    // vacant but reserved, making it unbookable despite totalVacant > 0.
    if (existingUnitType.totalAvailable === 0 && unitType.totalAvailable > 0) {
      groups[group] = unitType;
      continue;
    }
    if (existingUnitType.totalAvailable > 0 && unitType.totalAvailable === 0) {
      continue;
    }

    // Within same availability tier, prefer units that meet filter criteria
    if (preferFn) {
      const existingPreferred = preferFn(existingUnitType);
      const newPreferred = preferFn(unitType);
      if (!existingPreferred && newPreferred) {
        groups[group] = unitType;
        continue;
      }
      if (existingPreferred && !newPreferred) {
        continue;
      }
    }

    // Within same tier, cheapest wins
    if (unitType.rates.rate < existingUnitType.rates.rate) {
      groups[group] = unitType;
    }
  }

  return groups;
}
